import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { enableContactUsBackendSubmit } from '@gonfalon/dogfood-flags/dogfood-flags';
import { NewTabLink } from '@gonfalon/launchpad-experimental/NewTabLink';
import { trackContactSalesFormSubmitted, trackContactSalesLinkClicked } from '@gonfalon/marketo/analytics';
import { useMarketoForm } from '@gonfalon/marketo/useMarketoForm';
import { useSubmitContactUs } from '@gonfalon/rest-api/queries/contactUs';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogTrigger,
  FieldError,
  Form,
  Heading,
  IconButton,
  Input,
  Label,
  Modal,
  ModalOverlay,
  TextArea,
  TextField,
  toastQueue,
} from '@launchpad-ui/components';
import cx from 'clsx';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { type Plan } from '../types';

import styles from './ContactUs.module.css';

export const MARKETO_FORM_ID = '1941';

function RequiredAsterisk() {
  return <span aria-hidden="true"> *</span>;
}

/** Values align with Marketo Form 1941 `cta_source` routing (product contact flows). */
export type ContactUsSource =
  | 'trial_bar'
  | 'plan_change'
  | 'guardian_quote'
  | 'ai_upgrade'
  | 'usage_alert'
  | 'enable_agentcontrol';

const FormSchema = z.object({
  email: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
  company: z.string().min(1, 'Company is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  jobTitle: z.string().optional(),
  moreDetails: z.string().optional(),
});

type FormData = z.infer<typeof FormSchema>;

export const ContactUs = ({
  plan,
  size = 'large',
  variant = 'default',
  cta,
  defaults,
  source,
}: {
  plan?: Plan;
  size?: 'large' | 'small' | 'medium';
  variant?: 'default' | 'primary' | 'minimal';
  cta?: string;
  defaults?: {
    moreDetails?: string;
  };
  source?: ContactUsSource;
}) => {
  const { profile, account } = useAccountContext();
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: submitToBackend, isPending } = useSubmitContactUs();

  const {
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    register,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    mode: 'all',
    defaultValues: {
      email: profile?.email || '',
      company: account?.organization || '',
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      jobTitle: '',
      moreDetails: defaults?.moreDetails || '',
    },
  });

  const { onSubmit: marketoSubmit } = useMarketoForm({
    formId: MARKETO_FORM_ID,
  });

  const buttonCTA = () => cta ?? 'Contact us';

  const onConfirmModal = () => {
    const { email, company, firstName, lastName, jobTitle, moreDetails } = getValues();

    if (enableContactUsBackendSubmit()) {
      submitToBackend(
        {
          body: {
            email,
            firstName,
            lastName,
            company,
            jobTitle,
            description: moreDetails,
            referrerUrl: window.location.href,
            contactUsSource: source,
            cta,
            planType: plan?.planType,
          },
        },
        {
          onSuccess: () => {
            trackContactSalesFormSubmitted({
              planType: plan?.planType,
              component: source,
              cta,
              ...(moreDetails ? { description: moreDetails } : {}),
            });
            toastQueue.add({ title: 'An expert will be in touch as soon as possible.', status: 'success' });
            onCloseModal();
          },
          onError: () => {
            toastQueue.add({
              title:
                'Something went wrong submitting your request. Please email trials@launchdarkly.com directly and our team will be in touch.',
              status: 'error',
            });
          },
        },
      );
    } else {
      trackContactSalesFormSubmitted({
        planType: plan?.planType,
        component: source,
        cta,
        ...(moreDetails ? { description: moreDetails } : {}),
      });
      marketoSubmit({
        // I reverse-engineered these property names (Email, Company, ..., Description__c) from view-source
        // of the existing marketo form. These properties are defined in marketo and we must conform to them.
        Email: email,
        Company: company,
        FirstName: firstName,
        LastName: lastName,
        Title: jobTitle,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Description__c: moreDetails,
      });
      toastQueue.add({ title: 'An expert will be in touch as soon as possible.', status: 'success' });
      onCloseModal();
    }
  };

  const onCloseModal = () => {
    reset();
    setIsOpen(false);
  };

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (!open) {
      reset();
    }
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Button
        data-test-id="contact-us-button"
        variant={variant}
        className={cx(
          styles.contactUsButton,
          variant === 'minimal' && styles.minimal,
          variant === 'primary' && styles.primary,
        )}
        size={size}
        onPress={() => {
          trackContactSalesLinkClicked({ planType: plan?.planType, component: source, cta });
          setIsOpen(true);
        }}
      >
        {buttonCTA()}
      </Button>
      <ModalOverlay>
        <Modal>
          <Dialog aria-label="Contact LaunchDarkly">
            {() => (
              <>
                <Form className={styles.form} onSubmit={handleSubmit(onConfirmModal)}>
                  <div slot="header">
                    <Heading slot="title">Reach out to build your perfect plan</Heading>
                    <IconButton
                      aria-label="close"
                      icon="cancel"
                      size="small"
                      variant="minimal"
                      onPress={onCloseModal}
                    />
                  </div>
                  <div className={styles.formBody} slot="body">
                    <div>
                      <TextField aria-label="Email" isInvalid={!!errors.email} defaultValue={profile?.email}>
                        <Label>
                          Email <RequiredAsterisk />
                        </Label>
                        <Input
                          data-test-id="contact-us-email"
                          type="email"
                          placeholder="myemail@email.com"
                          {...register('email')}
                        />
                        {errors.email && <FieldError>{errors.email.message}</FieldError>}
                      </TextField>
                    </div>
                    <div>
                      <TextField aria-label="Company" isInvalid={!!errors.company} defaultValue={account?.organization}>
                        <Label>
                          Company <RequiredAsterisk />
                        </Label>
                        <Input data-test-id="contact-us-company" type="text" {...register('company')} />
                        {errors.company && <FieldError>{errors.company?.message}</FieldError>}
                      </TextField>
                    </div>
                    <div>
                      <TextField
                        aria-label="First name"
                        isInvalid={!!errors.firstName}
                        defaultValue={profile?.firstName}
                      >
                        <Label>
                          First name <RequiredAsterisk />
                        </Label>
                        <Input data-test-id="contact-us-first-name" type="text" {...register('firstName')} />
                        {errors.firstName && <FieldError>{errors.firstName?.message}</FieldError>}
                      </TextField>
                    </div>
                    <div>
                      <TextField aria-label="Last name" isInvalid={!!errors.lastName} defaultValue={profile?.lastName}>
                        <Label>
                          Last name <RequiredAsterisk />
                        </Label>
                        <Input data-test-id="contact-us-last-name" type="text" {...register('lastName')} />
                        {errors.lastName && <FieldError>{errors.lastName?.message}</FieldError>}
                      </TextField>
                    </div>
                    <div>
                      <TextField aria-label="Job title">
                        <Label>Job title</Label>
                        <Input data-test-id="contact-us-job-title" type="text" {...register('jobTitle')} />
                      </TextField>
                    </div>
                    <div className={styles.wide}>
                      <TextField aria-label="More details" defaultValue={defaults?.moreDetails}>
                        <Label>More details</Label>
                        <TextArea
                          data-test-id="contact-us-more-details"
                          className={styles.moreDetailsTextArea}
                          {...register('moreDetails')}
                        />
                      </TextField>
                    </div>
                    <div className={cx(styles.wide, styles.disclaimer)}>
                      By submitting, I authorize LaunchDarkly to contact me with personalized marketing communications
                      about our products and services. See our{' '}
                      <NewTabLink
                        href="https://launchdarkly.com/policies/privacy/"
                        text="Privacy Policy"
                        showIcon={false}
                      />{' '}
                      or{' '}
                      <NewTabLink
                        href="https://go.launchdarkly.com/UnsubscribePage.html"
                        text="Opt-Out"
                        showIcon={false}
                      />{' '}
                      at any time.
                    </div>
                  </div>
                  <div slot="footer">
                    <Button onPress={onCloseModal}>Cancel</Button>
                    <Button
                      variant="primary"
                      type="submit"
                      data-test-id="contact-us-submit-button"
                      aria-label="Submit contact us form"
                      isDisabled={!isValid || isPending}
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
